// src/strategy/CheckmateChecker.ts
import { BoardState, Position } from '../model/BoardState';
import { Piece } from '../model/Piece';
import logger from '../utils/Logger';

export class CheckmateChecker {
  private readonly boardState: BoardState;

  public constructor(boardState: BoardState) {
    this.boardState = boardState;
  }

  public isCheckmated(turn: 'Player' | 'Computer'): boolean {
    const playerColor = turn === 'Player' ? 'black' : 'white';
    const kingPosition = this.boardState.pieces.find(piece => piece.type === 'king' && piece.color === playerColor)?.position;

    if (!kingPosition) {
      logger.debug(`No king found for ${turn}`);
      return false;
    }

    // Check if the king is in check
    const inCheck = this.isInCheck(turn).inCheck;
    if (!inCheck) {
      // logger.debug(`${turn} is not in check.`);
      return false;
    }

    // Check if there are any legal moves to get out of check
    for (const piece of this.boardState.pieces) {
      if (piece.color === playerColor) {
        const legalMoves = piece.moveStrategy.getLegalMoves(piece.position, this.boardState);
        for (const move of legalMoves) {
          const simulatedBoardState = this.simulateMove(piece.position, move);
          if (!this.isInCheckAfterMove(turn, simulatedBoardState)) {
            logger.debug(`${turn} can move ${piece.type} from (${piece.position.row}, ${piece.position.col}) to (${move.row}, ${move.col}) to escape check.`);
            return false;
          }
        }
      }
    }

    logger.debug(`${turn} is in checkmate.`);
    return true;
  }

  public isStalemate(turn: 'Player' | 'Computer'): boolean {
    const playerColor = turn === 'Player' ? 'black' : 'white';
    const kingPosition = this.boardState.pieces.find(piece => piece.type === 'king' && piece.color === playerColor)?.position;

    if (!kingPosition) {
      logger.debug(`No king found for ${turn}`);
      return false;
    }

    // Check if the king is in check - if in check, it's not stalemate
    const inCheck = this.isInCheck(turn).inCheck;
    if (inCheck) {
      return false;
    }

    // Check if there are any legal moves - if there are none, it's stalemate
    for (const piece of this.boardState.pieces) {
      if (piece.color === playerColor) {
        const legalMoves = piece.moveStrategy.getLegalMoves(piece.position, this.boardState);
        
        // If any legal move exists, check if it would put the king in check
        for (const move of legalMoves) {
          const simulatedBoardState = this.simulateMove(piece.position, move);
          if (!this.isInCheckAfterMove(turn, simulatedBoardState)) {
            // Found at least one legal move that doesn't put/leave the king in check
            return false;
          }
        }
      }
    }

    logger.debug(`${turn} has no legal moves but is not in check - stalemate.`);
    return true;
  }

  public isInCheck(turn: 'Player' | 'Computer'): { inCheck: boolean, piece: Piece | null, position: Position | null } {
    const playerColor = turn === 'Player' ? 'black' : 'white';
    const opponentColor = turn === 'Player' ? 'white' : 'black';
    const kingPosition = this.boardState.pieces.find(piece => piece.type === 'king' && piece.color === playerColor)?.position;

    if (!kingPosition) return { inCheck: false, piece: null, position: null };

    for (const piece of this.boardState.pieces) {
      if (piece.color === opponentColor && this.canMoveTo(piece, kingPosition, this.boardState)) {
        logger.debug(`${opponentColor} ${piece.type} at (${piece.position.row}, ${piece.position.col}) can move to ${playerColor} king's position (${kingPosition.row}, ${kingPosition.col}).`);
        return { inCheck: true, piece, position: piece.position };
      }
    }

    return { inCheck: false, piece: null, position: null };
  }

  isInCheckAfterMove(turn: 'Player' | 'Computer', boardState: BoardState): boolean {
    const playerColor = turn === 'Player' ? 'black' : 'white';
    const opponentColor = turn === 'Player' ? 'white' : 'black';
    const kingPosition = boardState.pieces.find(piece => piece.type === 'king' && piece.color === playerColor)?.position;

    if (!kingPosition) return false;

    return boardState.pieces.some(piece => piece.color === opponentColor && this.canMoveTo(piece, kingPosition, boardState));
  }

  private canMoveTo(piece: Piece, position: Position, boardState: BoardState): boolean {
    const legalMoves = piece.moveStrategy.getLegalMoves(piece.position, boardState);
    return legalMoves.some(move => move.row === position.row && move.col === position.col);
  }

  private simulateMove(from: Position, to: Position): BoardState {
    const simulatedBoardState = new BoardState(
      this.boardState.pieces.map(piece => ({
        ...piece,
        position: piece.position.row === from.row && piece.position.col === from.col ? to : piece.position,
        moveStrategy: Object.create(Object.getPrototypeOf(piece.moveStrategy), Object.getOwnPropertyDescriptors(piece.moveStrategy))
      }))
    );

    // Remove any captured piece at the destination position
    simulatedBoardState.pieces = simulatedBoardState.pieces.filter(piece => !(piece.position.row === to.row && piece.position.col === to.col && piece.position !== to));

    return simulatedBoardState;
  }

  public isDraw(): boolean {
    const playerPieces = this.boardState.pieces.filter(piece => piece.color === 'black');
    const computerPieces = this.boardState.pieces.filter(piece => piece.color === 'white');
    
    // Check if both players have only kings left
    const playerHasOnlyKing = playerPieces.length === 1 && playerPieces[0].type === 'king';
    const computerHasOnlyKing = computerPieces.length === 1 && computerPieces[0].type === 'king';
    
    if (playerHasOnlyKing && computerHasOnlyKing) {
      logger.debug('Draw detected: Both players have only kings remaining');
      return true;
    }
    
    return false;
  }
}
